import { useReducedMotion, motion } from 'framer-motion'
import {
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { Alert } from '../atoms/Alert'
import { Spinner } from '../atoms/Spinner'

/**
 * Current-year monthly + quarterly two-series chart pair — the shared,
 * animated component behind Variant Pricing (baseline cost vs selling
 * price) and Warehouse (stock In vs Out). Single source for the look +
 * motion: a framer-motion staggered card pop-in, a recharts eased line
 * draw, and a travelling glint along each line. All gated by
 * useReducedMotion.
 *
 * Callers map their buckets to DualChartPoint { label, a, b } and supply
 * the two series' names/colours + value formatters. Series A is drawn
 * first (thinner, label below); series B second (thicker, label above).
 */
export interface DualChartPoint {
  readonly label: string
  readonly a: number
  /** Second series — omit (or undefined) for a single-series chart. */
  readonly b?: number
}

export interface DualSeries {
  readonly name: string
  readonly color: string
}

interface Props {
  readonly year: number | null
  readonly monthly: ReadonlyArray<DualChartPoint>
  readonly quarterly: ReadonlyArray<DualChartPoint>
  readonly isLoading: boolean
  readonly error: string | null
  readonly onRetry: () => void
  readonly seriesA: DualSeries
  /** Omit to render a single-series chart (only seriesA). */
  readonly seriesB?: DualSeries
  /** Word after the period in the card title — e.g. 'average' or 'total'. */
  readonly aggregateLabel: string
  /** Tooltip + point-label formatter (precise). */
  readonly formatValue: (n: number) => string
  /** Y-axis + compact point formatter. */
  readonly formatAxis: (n: number) => string
  /** Shown in a card when its bucket array is empty. */
  readonly emptyNote: string
}

const GRID_COLOR = '#E8DCC8'

export function CurrentYearDualCharts({
  year,
  monthly,
  quarterly,
  isLoading,
  error,
  onRetry,
  seriesA,
  seriesB,
  aggregateLabel,
  formatValue,
  formatAxis,
  emptyNote
}: Props): JSX.Element {
  if (error !== null) {
    return (
      <div className="border border-border rounded-md p-4 bg-bg-primary">
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={onRetry}
          >
            Retry
          </button>
        </Alert>
      </div>
    )
  }
  if (isLoading && monthly.length === 0 && quarterly.length === 0) {
    return (
      <div className="border border-border rounded-md p-4 bg-bg-primary flex items-center justify-center h-[280px]">
        <Spinner />
      </div>
    )
  }

  const yearLabel = year !== null ? ` (${year})` : ''
  // Spread seriesB only when defined — exactOptionalPropertyTypes rejects
  // `seriesB: undefined` against `seriesB?: DualSeries`.
  const shared = seriesB === undefined
    ? { seriesA, formatValue, formatAxis, emptyNote }
    : { seriesA, seriesB, formatValue, formatAxis, emptyNote }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ChartCard title={`Monthly ${aggregateLabel}${yearLabel}`} data={monthly} index={0} {...shared} />
      <ChartCard title={`Quarterly ${aggregateLabel}${yearLabel}`} data={quarterly} index={1} {...shared} />
    </div>
  )
}

function ChartCard({
  title,
  data,
  index,
  seriesA,
  seriesB,
  formatValue,
  formatAxis,
  emptyNote
}: {
  readonly title: string
  readonly data: ReadonlyArray<DualChartPoint>
  readonly index: number
  readonly seriesA: DualSeries
  readonly seriesB?: DualSeries
  readonly formatValue: (n: number) => string
  readonly formatAxis: (n: number) => string
  readonly emptyNote: string
}): JSX.Element {
  const hasB = seriesB !== undefined
  const reduce = useReducedMotion() === true
  const isEmpty = data.length === 0
  // framer-motion handles the CARD entrance (staggered spring pop-in);
  // recharts draws the LINES (A first, B shortly after) once in.
  const lineBegin = reduce ? 0 : 300 + index * 130
  return (
    <motion.div
      className="border border-border rounded-md p-4 bg-bg-primary"
      initial={reduce ? false : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 17, delay: index * 0.1 }}
    >
      <header className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading text-h3 text-charcoal">{title}</h2>
        <span className="font-body text-body-xs text-text-secondary">
          {isEmpty ? 'no data yet' : `${data.length} bucket${data.length === 1 ? '' : 's'}`}
        </span>
      </header>
      <div className="h-[240px]">
        {isEmpty ? (
          <div className="h-full flex items-center justify-center font-body text-body-sm text-text-secondary">
            {emptyNote}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data as DualChartPoint[]} margin={{ top: 8, right: 12, bottom: 8, left: 4 }}>
              {/* Travelling white band that loops ALONG each line (a glint
                  confined to the line, not the whole card). userSpaceOnUse
                  (pixel space) so flat lines glint too; staggered per card +
                  per series. */}
              {reduce ? null : (
                <defs>
                  <linearGradient id={`glintA-${index}`} gradientUnits="userSpaceOnUse" x1="-320" y1="0" x2="-160" y2="0">
                    <stop offset="0" stopColor="#FFFFFF" stopOpacity="0" />
                    <stop offset="0.5" stopColor="#FFFFFF" stopOpacity="0.9" />
                    <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
                    <animate attributeName="x1" values="-320;1600" dur="2.6s" begin={`${index * 0.7}s`} repeatCount="indefinite" />
                    <animate attributeName="x2" values="-160;1760" dur="2.6s" begin={`${index * 0.7}s`} repeatCount="indefinite" />
                  </linearGradient>
                  <linearGradient id={`glintB-${index}`} gradientUnits="userSpaceOnUse" x1="-320" y1="0" x2="-160" y2="0">
                    <stop offset="0" stopColor="#FFFFFF" stopOpacity="0" />
                    <stop offset="0.5" stopColor="#FFFFFF" stopOpacity="0.95" />
                    <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
                    <animate attributeName="x1" values="-320;1600" dur="2.6s" begin={`${index * 0.7 + 1.3}s`} repeatCount="indefinite" />
                    <animate attributeName="x2" values="-160;1760" dur="2.6s" begin={`${index * 0.7 + 1.3}s`} repeatCount="indefinite" />
                  </linearGradient>
                </defs>
              )}
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7A6E5F' }} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
              <YAxis
                tick={{ fontSize: 11, fill: '#7A6E5F' }}
                tickLine={false}
                axisLine={{ stroke: GRID_COLOR }}
                tickFormatter={formatAxis}
                width={52}
              />
              <Tooltip
                contentStyle={{ background: '#FAF5ED', border: `1px solid ${GRID_COLOR}`, borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#2C2416' }}
                formatter={(value, name) => [
                  typeof value === 'number' ? formatValue(value) : String(value),
                  name === 'a' ? seriesA.name : (seriesB?.name ?? '')
                ]}
              />
              <Legend
                verticalAlign="top"
                align="right"
                iconType="plainline"
                wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
                formatter={(value) => (value === 'a' ? seriesA.name : (seriesB?.name ?? ''))}
              />
              <Line
                type="monotone"
                dataKey="a"
                name="a"
                stroke={seriesA.color}
                strokeWidth={1.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                isAnimationActive={!reduce}
                animationBegin={lineBegin}
                animationDuration={reduce ? 0 : 600}
                animationEasing="ease-out"
              >
                <LabelList
                  dataKey="a"
                  position="bottom"
                  offset={8}
                  formatter={(v) => (typeof v === 'number' ? formatAxis(v) : '')}
                  style={{ fontSize: 10, fill: seriesA.color }}
                />
              </Line>
              {hasB && seriesB !== undefined ? (
                <Line
                  type="monotone"
                  dataKey="b"
                  name="b"
                  stroke={seriesB.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={!reduce}
                  animationBegin={reduce ? 0 : lineBegin + 250}
                  animationDuration={reduce ? 0 : 600}
                  animationEasing="ease-out"
                >
                  <LabelList
                    dataKey="b"
                    position="top"
                    offset={8}
                    formatter={(v) => (typeof v === 'number' ? formatAxis(v) : '')}
                    style={{ fontSize: 10, fill: '#2C2416', fontWeight: 600 }}
                  />
                </Line>
              ) : null}
              {/* Overlay lines carrying the travelling glint, excluded from legend + tooltip. */}
              {reduce ? null : (
                <Line type="monotone" dataKey="a" stroke={`url(#glintA-${index})`} strokeWidth={3} dot={false} activeDot={false} legendType="none" tooltipType="none" isAnimationActive={false} />
              )}
              {reduce || !hasB ? null : (
                <Line type="monotone" dataKey="b" stroke={`url(#glintB-${index})`} strokeWidth={3.5} dot={false} activeDot={false} legendType="none" tooltipType="none" isAnimationActive={false} />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </motion.div>
  )
}
